import React from "react";
import "./App.scss";
import { BrowserRouter, Redirect, Route, Switch, useLocation } from "react-router-dom";
import Env from "./utils/env";
import AlertProvider from "components/shared/AlertComponent/AlertContext";
import AlertComponent from "components/shared/AlertComponent/AlertComponent";
import { RedirectToOnboardingPage } from "./utils/redirect";
import { Logger } from "./utils/logger";
import { Bullseye, Spinner } from "@patternfly/react-core";
import * as Sentry from "@sentry/react";
import { getUserInfoFromJWT } from "./utils/fetch";
import { getCwfConfig, getOnboardingStatus } from "./api/hydra";
import { useFetch } from "./hooks";
import { RHCAuthProvider, useRHCAuthContext } from "@rhc-shared-components/rhc-sso";
import { ControllerContextProvider } from "@rhc-shared-components/control-wrapper";
import { QueryClient, QueryClientProvider } from "react-query";
import StatusErrorPage from "components/shared/StatusErrorPage/StatusErrorPage";
import { CertWorkflowApp } from "./components/CertWorkflowApp/CertWorkflowApp";
import { checkAndSendAnalytics } from '@rhc-shared-components/rhc-eddl';
import { CWRouteParams } from "./constants/router";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
            retry: false,
            refetchIntervalInBackground: false,
            refetchInterval: false,
        },
    },
});

const PLSRedirectComponent = ({url}:{url: string}) => {
    window.location.href = url;
    return (
        <div className="redirect-text">
            <Bullseye>
                Redirecting to the new url, please update your bookmarks...
            </Bullseye>
        </div>
    );
};
const AuthenticatedApp: React.FC = () => {
    const {isAuthenticated} = useRHCAuthContext(); // Auth status
    const [isLoading, setIsLoading] = React.useState(true); // Loading status
    const [errorStatusCode, setErrorStatusCode] = React.useState(null); // Loading error status
    const {request: onboardingStatusRequest} = useFetch(getOnboardingStatus, {propagateErrors: true});
    const renderApp = React.useCallback(async () => {
        Logger("User is authenticated");
        if (Env.getEnvName() === Env.EnvNames.PROD) {
            Sentry.setUser(getUserInfoFromJWT());
        }
        try {
            const onboardingStatus = await onboardingStatusRequest();
            if (onboardingStatus && onboardingStatus["status"] === "completed") {
                Logger("User is onboarded, enabling the app");
                setIsLoading(false);
            } else {
                Logger("User is not onboarded, redirecting to tnc page");
                RedirectToOnboardingPage();
            }
        } catch(error) {
            if (error?.status || error.status !== 200) {
                setErrorStatusCode(error.status);
              }
              setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[])
    React.useEffect(()=>{
        if (isAuthenticated && isLoading) {
          renderApp();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[isAuthenticated, renderApp]);
    const { pathname } = useLocation<CWRouteParams>();
    React.useEffect(()=>{
        if (isAuthenticated) {
            checkAndSendAnalytics({ spaName: 'certification-workflow' });
        }
    },[isAuthenticated, pathname]);
    return (
        <div className="app">
            {isLoading ?
                <Bullseye><Spinner/></Bullseye> :
                <ControllerContextProvider appKey={'cwf_config'} configApi={getCwfConfig} bannerClassName="rhc-center">
                    <span className="main-app">
                        <AlertComponent/>
                        <Switch>
                            {errorStatusCode !== null && (
                              <Route
                                render={() => (
                                  <StatusErrorPage errorStatus={errorStatusCode} />
                                )}
                              />
                            )}
                            <Route
                              path={Env.routePrefix}
                              render={() => (<PLSRedirectComponent url={Env.productListingUrl + "/components"} />)}
                              exact={true}
                            />
                            <Route
                              path={Env.routePrefix + "/create/:project_type"}
                              render={() => (<PLSRedirectComponent url={Env.productListingUrl + "/products"} />)}
                              exact={true}
                            />
                            <Redirect from={`${Env.routePrefix}/:oldProductId/overview`}
                                      to={`${Env.routePrefix}/view/:oldProductId`} exact />
                            <Redirect from={`${Env.routePrefix}/:oldProductId/images`}
                                      to={`${Env.routePrefix}/view/:oldProductId/images`} exact />
                            <Redirect from={`${Env.routePrefix}/:oldProductId/settings`}
                                      to={`${Env.routePrefix}/view/:oldProductId/repository-info`} exact />
                            <Redirect from={`${Env.routePrefix}/:oldProductId/test-results`}
                                      to={`${Env.routePrefix}/view/:oldProductId`} exact />
                            <Redirect from={`${Env.routePrefix}/:oldProductId/update-graph`}
                                      to={`${Env.routePrefix}/view/:oldProductId/update-graph`} exact />
                            <Route
                              path={`${Env.oldRoutePrefix}`}
                              render={props => (<Redirect
                                to={`${props?.location?.pathname?.replace(Env.oldRoutePrefix, Env.routePrefix)}`} />)}
                            />
                            <Redirect from="/" to={Env.routePrefix + "/view/6508136a5bc646e3a7a22367/images"} exact />
                            <Route
                              path={`${Env.routePrefix}/:productId/:id/`}
                              component={CertWorkflowApp}
                              exact={false}
                            />
                            <Route
                              render={() => (
                                <StatusErrorPage errorStatus={404} />
                              )}
                            />
                        </Switch>
                        <div id="modal-div-container"/>
                    </span>
                </ControllerContextProvider>}
        </div>
    );
};

const App: React.FC = () => (<QueryClientProvider client={queryClient}>
    <RHCAuthProvider>
        <AlertProvider>
            <BrowserRouter>
                <AuthenticatedApp/>
            </BrowserRouter>
        </AlertProvider>
    </RHCAuthProvider>
</QueryClientProvider>)

export default App;
